import { useEffect, useRef, useState } from "react";
import {
  collection, getDocs, addDoc, deleteDoc,
  doc, updateDoc, serverTimestamp, orderBy, query,
} from "../services/mysqlStore";
import {
  ref, uploadBytesResumable, getDownloadURL, storage,
} from "../services/mysqlStorage";
import { db } from "../services/mysqlStore";
import { DEFAULT_NAV_GROUPS } from "../constants/navCategories";

const TOP_LEVEL_CATEGORIES = DEFAULT_NAV_GROUPS.map(g => g.label);

const EMPTY_FORM = {
  name: "",
  category: TOP_LEVEL_CATEGORIES[0] || "Promotions & Apparel",
  description: "",
  startingPrice: "",
  featured: true,
  enabled: true,
};

const EMPTY_OPTION_GROUP = { groupName: "", options: "" };

const UploadIcon = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>;
const TrashIcon = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>;
const PlusIcon = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>;
const ImgIcon = () => <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>;

function uploadFile(file, path, onProgress) {
  return new Promise((resolve, reject) => {
    const storageRef = ref(storage, path);
    const task = uploadBytesResumable(storageRef, file);
    task.on("state_changed",
      snap => onProgress(Math.round(snap.bytesTransferred / snap.totalBytes * 100)),
      reject,
      () => getDownloadURL(task.snapshot.ref).then(resolve).catch(reject)
    );
  });
}

export default function AdminCustomProducts() {
  const [products, setProducts]     = useState([]);
  const [loading, setLoading]       = useState(true);
  const [editingId, setEditingId]   = useState(null);
  const [form, setForm]             = useState(EMPTY_FORM);
  const [images, setImages]         = useState([]);        // [{ file, preview, url, uploading, progress }]
  const [existingImages, setExistingImages] = useState([]); // already saved URLs
  const [optionGroups, setOptionGroups]     = useState([]);  // [{ groupName, options }]
  const [saving, setSaving]         = useState(false);
  const [msg, setMsg]               = useState("");
  const fileRef = useRef();

  const load = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(query(collection(db, "customProducts"), orderBy("createdAt", "desc")));
      setProducts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setImages([]);
    setExistingImages([]);
    setOptionGroups([]);
    setEditingId(null);
    setMsg("");
  };

  const handleEdit = (p) => {
    setEditingId(p.id);
    setForm({
      name:          p.name          || "",
      category:      p.category      || "Promotional Products",
      description:   p.description   || "",
      startingPrice: p.startingPrice || "",
      featured:      p.featured      !== false,
      enabled:       p.enabled       !== false,
    });
    setExistingImages(p.images || []);
    setImages([]);
    setOptionGroups(
      (p.optionGroups || []).map(g => ({
        groupName: g.groupName || "",
        options: Array.isArray(g.options) ? g.options.join(", ") : (g.options || ""),
      }))
    );
    setMsg("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Image file select
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const newImgs = files.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      url: null,
      uploading: false,
      progress: 0,
    }));
    setImages(prev => [...prev, ...newImgs]);
    e.target.value = "";
  };

  // Remove new image before upload
  const removeNewImage = (i) => {
    setImages(prev => prev.filter((_, idx) => idx !== i));
  };

  // Remove existing saved image
  const removeExistingImage = (i) => {
    setExistingImages(prev => prev.filter((_, idx) => idx !== i));
  };

  // Option groups
  const addOptionGroup = () => setOptionGroups(prev => [...prev, { ...EMPTY_OPTION_GROUP }]);
  const removeOptionGroup = (i) => setOptionGroups(prev => prev.filter((_, idx) => idx !== i));
  const updateOptionGroup = (i, field, val) => {
    setOptionGroups(prev => prev.map((g, idx) => idx === i ? { ...g, [field]: val } : g));
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setMsg("error:Product name is required"); return; }
    setSaving(true);
    setMsg("");
    try {
      // Upload new images
      const uploadedUrls = await Promise.all(
        images.map(async (img, i) => {
          if (img.url) return img.url;
          return await uploadFile(
            img.file,
            `custom-products/${Date.now()}_${i}_${img.file.name}`,
            (p) => setImages(prev => prev.map((im, idx) => idx === i ? { ...im, progress: p } : im))
          );
        })
      );

      // Build option groups — parse comma-separated options
      const parsedGroups = optionGroups
        .filter(g => g.groupName.trim())
        .map(g => ({
          groupName: g.groupName.trim(),
          options: Array.isArray(g.options)
            ? g.options.map(o => String(o).trim()).filter(Boolean)
            : String(g.options || "").split(",").map(o => o.trim()).filter(Boolean),
        }));

      const allImages = [...existingImages, ...uploadedUrls];
      const imageUrl  = allImages[0] || "";

      const data = {
        name:          form.name.trim(),
        category:      form.category.trim() || "Promotional Products",
        description:   form.description.trim(),
        startingPrice: form.startingPrice ? parseFloat(form.startingPrice) : null,
        featured:      form.featured,
        enabled:       form.enabled,
        images:        allImages,
        imageUrl,
        optionGroups:  parsedGroups,
        isCustom:      true,
      };

      if (editingId) {
        await updateDoc(doc(db, "customProducts", editingId), data);
        setMsg("success:Product updated!");
      } else {
        await addDoc(collection(db, "customProducts"), { ...data, createdAt: serverTimestamp() });
        setMsg("success:Product added!");
      }

      resetForm();
      load();
    } catch (e) {
      console.error(e);
      const code = e?.code || "";
      let friendly = "Failed to save. Please try again.";
      if (code === "permission-denied") {
        friendly = "Failed to save: permission denied. Aap ka Firestore/Storage security rules 'customProducts' collection ke liye admin write allow nahi kar rahe.";
      } else if (e?.message) {
        friendly = `Failed to save: ${e.message}`;
      }
      setMsg("error:" + friendly);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await deleteDoc(doc(db, "customProducts", id));
      load();
    } catch (e) { console.error(e); }
  };

  const toggleField = async (id, field, val) => {
    try {
      await updateDoc(doc(db, "customProducts", id), { [field]: !val });
      setProducts(prev => prev.map(p => p.id === id ? { ...p, [field]: !val } : p));
    } catch (e) { console.error(e); }
  };

  const isErr = msg.startsWith("error:");
  const msgText = msg.replace(/^(success|error):/, "");

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>

      {/* ── Form ─────────────────────────────────────────────────── */}
      <div className="adm-card">
        <div className="adm-card-header">
          <span className="adm-card-title">{editingId ? "Edit Product" : "Add Promotional Product"}</span>
        </div>
        <div style={{ padding: 22 }}>

          {/* Basic Info */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
            <div className="adm-form-group" style={{ gridColumn: "1 / -1" }}>
              <label>Product Name *</label>
              <input
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Custom Branded Tote Bags"
              />
            </div>
            <div className="adm-form-group">
              <label>Category</label>
              <select
                value={form.category}
                onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                style={inputStyle}
              >
                {TOP_LEVEL_CATEGORIES.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
              <span style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", marginTop: 4 }}>
                Site ki top-level category chunain
              </span>
            </div>
            <div className="adm-form-group">
              <label>Starting Price (CAD)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={form.startingPrice}
                onChange={e => setForm(f => ({ ...f, startingPrice: e.target.value }))}
                placeholder="e.g. 29.99"
              />
            </div>
            <div className="adm-form-group" style={{ gridColumn: "1 / -1" }}>
              <label>Description</label>
              <textarea
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Short product description shown on product page..."
                rows={3}
                style={{ width: "100%", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#fff", padding: "10px 12px", fontSize: 13, fontFamily: "var(--font-main)", resize: "vertical", outline: "none", lineHeight: 1.6, boxSizing: "border-box" }}
              />
            </div>
          </div>

          {/* Toggles */}
          <div style={{ display: "flex", gap: 24, marginBottom: 20 }}>
            {[
              { key: "featured", label: "Show on Homepage (Featured)" },
              { key: "enabled",  label: "Visible on Products Page" },
            ].map(({ key, label }) => (
              <label key={key} style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
                <div
                  onClick={() => setForm(f => ({ ...f, [key]: !f[key] }))}
                  style={{
                    width: 42, height: 24, borderRadius: 12,
                    background: form[key] ? "var(--cyan)" : "rgba(255,255,255,0.15)",
                    position: "relative", transition: "background 0.2s", cursor: "pointer", flexShrink: 0,
                  }}
                >
                  <span style={{
                    position: "absolute", top: 2,
                    left: form[key] ? 20 : 2,
                    width: 20, height: 20, borderRadius: "50%",
                    background: "#fff", transition: "left 0.2s",
                  }} />
                </div>
                <span style={{ color: "rgba(255,255,255,0.7)", fontSize: 13 }}>{label}</span>
              </label>
            ))}
          </div>

          {/* Images */}
          <div className="adm-form-group" style={{ marginBottom: 20 }}>
            <label>Product Images</label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 10 }}>
              {/* Existing images */}
              {existingImages.map((url, i) => (
                <div key={i} style={{ position: "relative", width: 90, height: 80 }}>
                  <img src={url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 8, border: "1px solid rgba(255,255,255,0.1)" }} />
                  <button onClick={() => removeExistingImage(i)} style={{ position: "absolute", top: -6, right: -6, width: 20, height: 20, borderRadius: "50%", background: "#e91e8c", border: "none", color: "#fff", fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>
                </div>
              ))}
              {/* New images */}
              {images.map((img, i) => (
                <div key={i} style={{ position: "relative", width: 90, height: 80 }}>
                  <img src={img.preview} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 8, border: "1px solid rgba(255,255,255,0.1)" }} />
                  {img.progress > 0 && img.progress < 100 && (
                    <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 4, background: "rgba(0,0,0,0.4)", borderRadius: "0 0 8px 8px" }}>
                      <div style={{ height: "100%", width: img.progress + "%", background: "var(--cyan)", borderRadius: "inherit", transition: "width 0.2s" }} />
                    </div>
                  )}
                  <button onClick={() => removeNewImage(i)} style={{ position: "absolute", top: -6, right: -6, width: 20, height: 20, borderRadius: "50%", background: "#e91e8c", border: "none", color: "#fff", fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>
                </div>
              ))}
              {/* Upload button */}
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                style={{ width: 90, height: 80, borderRadius: 8, border: "1.5px dashed rgba(255,255,255,0.2)", background: "rgba(255,255,255,0.04)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6, cursor: "pointer", color: "rgba(255,255,255,0.3)" }}
              >
                <ImgIcon />
                <span style={{ fontSize: 10 }}>Add Image</span>
              </button>
            </div>
            <input ref={fileRef} type="file" accept="image/*" multiple style={{ display: "none" }} onChange={handleFileSelect} />
            <p style={{ fontSize: 11, color: "rgba(255,255,255,0.3)" }}>First image will be the main product image. Multiple images supported.</p>
          </div>

          {/* Option Groups */}
          <div className="adm-form-group" style={{ marginBottom: 20 }}>
            <label>Product Options <span style={{ color: "rgba(255,255,255,0.3)", fontWeight: 400 }}>(e.g. Size, Qty, Color)</span></label>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {optionGroups.map((g, i) => (
                <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 10, padding: 12 }}>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8, flex: 1 }}>
                    <input
                      value={g.groupName}
                      onChange={e => updateOptionGroup(i, "groupName", e.target.value)}
                      placeholder="Group name (e.g. Size)"
                      style={inputStyle}
                    />
                    <input
                      value={g.options}
                      onChange={e => updateOptionGroup(i, "options", e.target.value)}
                      placeholder="Options separated by commas (e.g. Small, Medium, Large)"
                      style={inputStyle}
                    />
                  </div>
                  <button onClick={() => removeOptionGroup(i)} style={{ background: "rgba(239,68,68,0.15)", border: "1px solid rgba(239,68,68,0.3)", color: "#f87171", padding: "8px 10px", borderRadius: 7, cursor: "pointer" }}>
                    <TrashIcon />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={addOptionGroup}
                style={{ alignSelf: "flex-start", display: "flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,0.05)", border: "1px dashed rgba(255,255,255,0.2)", borderRadius: 8, padding: "8px 14px", color: "rgba(255,255,255,0.5)", fontSize: 13, cursor: "pointer" }}
              >
                <PlusIcon /> Add Option Group
              </button>
            </div>
          </div>

          {/* Message */}
          {msg && (
            <div style={{ padding: "8px 14px", borderRadius: 6, fontSize: 13, marginBottom: 14, background: isErr ? "rgba(239,68,68,0.12)" : "rgba(0,170,221,0.12)", color: isErr ? "#f87171" : "var(--cyan)", border: `1px solid ${isErr ? "#f87171" : "var(--cyan)"}` }}>
              {msgText}
            </div>
          )}

          <div style={{ display: "flex", gap: 10 }}>
            <button className="adm-btn adm-btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : editingId ? "Update Product" : "+ Add Product"}
            </button>
            {editingId && (
              <button className="adm-btn" onClick={resetForm} style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.6)" }}>
                Cancel
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Products List ──────────────────────────────────────────── */}
      <div className="adm-card">
        <div className="adm-card-header">
          <span className="adm-card-title">Promotional Products ({products.length})</span>
        </div>
        {loading ? (
          <p style={{ padding: 24, color: "rgba(255,255,255,0.3)" }}>Loading...</p>
        ) : products.length === 0 ? (
          <p style={{ padding: 24, color: "rgba(255,255,255,0.3)" }}>No promotional products yet.</p>
        ) : (
          products.map(p => (
            <div key={p.id} style={{ display: "flex", gap: 14, alignItems: "center", padding: "14px 20px", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
              {/* Image */}
              <div style={{ width: 64, height: 56, borderRadius: 8, background: "rgba(255,255,255,0.06)", flexShrink: 0, overflow: "hidden" }}>
                {p.imageUrl
                  ? <img src={p.imageUrl} alt={p.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "rgba(255,255,255,0.2)" }}><ImgIcon /></div>
                }
              </div>

              {/* Info */}
              <div style={{ flex: 1 }}>
                <strong style={{ color: "#fff", fontSize: 14 }}>{p.name}</strong>
                <div style={{ display: "flex", gap: 8, marginTop: 4, flexWrap: "wrap" }}>
                  <span style={tagStyle}>{p.category}</span>
                  {p.startingPrice && <span style={tagStyle}>From ${p.startingPrice}</span>}
                  {p.optionGroups?.length > 0 && <span style={tagStyle}>{p.optionGroups.length} option group{p.optionGroups.length > 1 ? "s" : ""}</span>}
                </div>
              </div>

              {/* Toggles */}
              <div style={{ display: "flex", gap: 14, alignItems: "center", flexShrink: 0 }}>
                <label style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, cursor: "pointer" }}>
                  <span style={{ fontSize: 10, color: "rgba(255,255,255,0.4)" }}>Featured</span>
                  <div onClick={() => toggleField(p.id, "featured", p.featured)} style={{ width: 36, height: 20, borderRadius: 10, background: p.featured !== false ? "var(--cyan)" : "rgba(255,255,255,0.15)", position: "relative", cursor: "pointer", transition: "background 0.2s" }}>
                    <span style={{ position: "absolute", top: 2, left: p.featured !== false ? 18 : 2, width: 16, height: 16, borderRadius: "50%", background: "#fff", transition: "left 0.2s" }} />
                  </div>
                </label>
                <label style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, cursor: "pointer" }}>
                  <span style={{ fontSize: 10, color: "rgba(255,255,255,0.4)" }}>Visible</span>
                  <div onClick={() => toggleField(p.id, "enabled", p.enabled)} style={{ width: 36, height: 20, borderRadius: 10, background: p.enabled !== false ? "var(--cyan)" : "rgba(255,255,255,0.15)", position: "relative", cursor: "pointer", transition: "background 0.2s" }}>
                    <span style={{ position: "absolute", top: 2, left: p.enabled !== false ? 18 : 2, width: 16, height: 16, borderRadius: "50%", background: "#fff", transition: "left 0.2s" }} />
                  </div>
                </label>
              </div>

              {/* Actions */}
              <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                <button className="adm-btn adm-btn-sm" onClick={() => handleEdit(p)} style={{ background: "rgba(0,170,221,0.12)", border: "1px solid var(--cyan)", color: "var(--cyan)" }}>Edit</button>
                <button className="adm-btn adm-btn-sm adm-btn-danger" onClick={() => handleDelete(p.id)}>Delete</button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

const inputStyle = {
  width: "100%", background: "rgba(255,255,255,0.05)",
  border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8,
  color: "#fff", padding: "9px 12px", fontSize: 13,
  fontFamily: "var(--font-main)", outline: "none", boxSizing: "border-box",
};
const tagStyle = {
  background: "rgba(255,255,255,0.07)", color: "rgba(255,255,255,0.6)",
  padding: "2px 9px", borderRadius: 20, fontSize: 11, fontWeight: 600,
};